/**
 * 악덕신고 상세 모달 (공용) — 알림 모아보기의 악덕 심사창과
 * 악덕마스터 모달(/adm/devil/masters)이 함께 사용한다.
 *
 *   $j.F.Admin.Devil.Report.open(num, { readOnly, modalId, labels, onChanged });
 *
 *  - readOnly=true  : 조회 전용(액션 버튼 없음) — 마스터 모달에서 사용
 *  - readOnly=false : 승인/반려/정정요청/정정심사/회수액 액션 포함 — 신고 심사 페이지
 *  - modalId        : JBModal id. 마스터 모달 위에 띄울 땐 다른 id 로 스택(예: 'devil-report-view')
 *  - labels         : { report, defense, master } 상태 라벨맵(blade @json(G5_DV_*) 주입)
 *  - onChanged      : 상태 변경(액션 성공) 시 호출되는 콜백(목록 새로고침 등)
 *
 * 데이터 단일 소스: GET /adm/ajax/devil/report/{num}
 * 동작 보존:
 *  - 정정요청 발송/재발송 → 모달을 닫지 않고 새로고침
 *  - 그 외 액션(승인/반려/정정심사/회수액) → 모달 닫고 onChanged
 */
import Util from '../../../Common/Util';
export default class Report {
    constructor() {
        this.modalId   = 'devil-report-detail';
        this.readOnly  = false;
        this.onChanged = null;
        this.host      = null; // 통합모달 임베드 호스트(있으면 JBModal 대신 host 컨테이너에 렌더)
        this._num      = 0;
        this._photosR  = []; // 신고 증빙
        this._photosD  = []; // 정정 증빙 (photo_type DEFENSE — 옛 이름)
        this._repEditing = false; // 대표번호 수정 모드(승인 전)
        this._styleInjected = false;
        this.labels    = { report: {}, defense: {}, master: { REG: '등록', RELEASED: '해제' } };

        this.LOG_LABELS = {
            'REPORT/AUTH':     '휴대폰 본인인증',
            'REPORT/SUBMIT':   '제보 제출',
            'REPORT/DUP_CONFIRM': '중복 경고 확인 후 제출',
            'REPORT/APPROVE':  '체불 등록',
            'REPORT/REJECT':   '제보 반려',
            'REPORT/RECALL':   '철회·해제',
            'REPORT/REP_PHONE': '대표번호 수정',
            'DEFENSE/NOTICE':  '소명요청 발송',
            'DEFENSE/AUTH':    '업체 휴대폰 본인인증',   // 업체가 정정 페이지에서 문자 인증을 통과한 시각(번호는 가려서)
            'DEFENSE/CALL':    '업체가 제보자 통화 연결(안심번호)',   // 소명 — 업체가 정정 페이지에서 [제보자와 통화]
            'DEFENSE/SUBMIT':  '소명 요청 제출',
            'DEFENSE/APPROVE': '소명 승인(구)',
            'DEFENSE/REJECT':  '소명 반려',
            'DEFENSE/EXPIRE':  '소명 기한 만료(자동등록)',
            'RECOVER/RECOVER': '회수 처리',
            'RECOVER/CANCEL':  '회수 취소',
        };

        this._bind();
    }

    // ── 진입점 ───────────────────────────────────────────────
    open = (num, opts = {}) => {
        if (opts.labels) this.labels = Object.assign({}, this.labels, opts.labels);
        this.readOnly = !!opts.readOnly;
        this.modalId  = opts.modalId || 'devil-report-detail';
        if (opts.onChanged !== undefined) this.onChanged = opts.onChanged;
        if (opts.host !== undefined) this.host = opts.host;
        if (num !== this._num) this._repEditing = false;   // 다른 신고으로 이동 시 대표번호 수정모드 해제
        this._num = num;
        $.get('/adm/ajax/devil/report/' + num, res => this._render(res), 'json');
    }

    // ── 렌더 ────────────────────────────────────────────────
    _render = (res) => {
        this._injectStyle();
        if (!res.result) { Util.swalert(res.message || '조회 실패'); return; }
        const r    = res.report;
        const num  = r.num;
        const info = r.bad_info ? (JSON.parse(r.bad_info) || {}) : {};
        const df   = res.defense || null;
        const RO   = this.readOnly;
        this._noticeDays = parseInt(res.notice_period_days, 10) || 7; // 정정 미응답 자동등록 안내일수

        // 증빙사진 — 신고(좌)/정정(우) 분리, 클릭 시 각자 갤러리
        const photosR = (res.photos || []).filter(p => p.photo_type === 'REPORT'  && p.image_url);
        const photosD = (res.photos || []).filter(p => p.photo_type === 'DEFENSE' && p.image_url);
        this._photosR = photosR.map(p => p.image_url);
        this._photosD = photosD.map(p => p.image_url);
        const thumbs = (arr, gal) => arr.length
            ? arr.map((p, i) => `<img src="${p.thumb_url || p.image_url}" class="dvr-photo" data-gal="${gal}" data-idx="${i}">`).join('')
            : '<span class="text-muted" style="font-size:13px">없음</span>';

        // ── 통합 진행바 : 스텝퍼(좌) + [단계별 버튼 · 처리내역 토글](우), 그 아래 처리내역 패널 ──
        const flowStage = this._flowStageHtml(r, df);
        // 악덕 등록 후 마스터가 해제(RELEASED)된 건은 신고쪽 액션(신고+정정)을 모두 숨긴다.
        // (해제 단계에서 신고쪽 버튼을 남기면 경우의 수가 과도하게 복잡해짐 — 종결로 취급)
        const released = (r.master_status === 'RELEASED');
        const flowActions = (RO || released) ? '' : (this._reportActions(r, df, RO) + this._defenseActions(r, df, RO));
        const logCount = (res.logs || []).length;
        const logBtn = `<button type="button" class="dvr-logbtn" data-view-ok>처리내역 <span class="dvr-logcount">${logCount}</span> <span class="dvr-caret">▾</span></button>`;
        const memoCount = (res.memos || []).length;
        const memoBtn = `<button type="button" class="dvr-memobtn" data-view-ok>메모내역 <span class="dvr-memocount">${memoCount}</span> <span class="dvr-caret">▾</span></button>`;
        // 처리내역 아코디언 행(기본 접힘) — 토글 버튼 바로 아래에 표시
        const repLogRows = (res.logs || []).map(l => {
            const act  = this._logAction(l.rel_table, l.rel_action);            // 예: 제보 제출 · 신고 승인 · 정정 요청 제출
            // 저장된 메모가 라벨과 같은 말이면 숨긴다 — 10월에 쌓인 '정정…' 메모도 지금 라벨('소명…')과 같은 것으로 친다
            const memo = (l.memo && String(l.memo).replace(/정정/g, '소명') !== act) ? ` · <span class="dvr-logmemo">${this._esc(l.memo)}</span>` : '';
            const cls  = l.rel_table === 'DEFENSE' ? ' dvr-logm-d' : l.rel_table === 'REPORT' ? ' dvr-logm-r' : '';
            return `
            <div class="dvr-logitem">
                <span class="dvr-logt">${this._esc((l.created_at || '').substr(0, 16))}</span>
                <span class="dvr-logm${cls}"><b>${act}</b>${memo}</span>
                <span class="dvr-logby">${this._logActor(l)}</span>
            </div>`;
        }).join('') || '<div class="dvr-logempty">이력 없음</div>';
        const flowBar = `
            <div class="dvr-flow">
                <div class="dvr-flow-top">
                    <div class="dvr-stage">${flowStage}</div>
                    <div class="dvr-flow-right">
                        ${memoBtn}
                        ${logBtn}
                    </div>
                </div>
                ${flowActions ? `<div class="dvr-actions">${flowActions}</div>` : ''}
                <div class="dvr-replog" style="display:none">
                    <div class="dvr-replog-t"><span class="bar"></span>이 제보건 변경이력 <small>(제보·소명 통합)</small></div>
                    <div class="dvr-replog-list">${repLogRows}</div>
                </div>
                <div class="dvr-memobox" style="display:none">
                    <div class="dvr-replog-t"><span class="bar" style="background:#e8863b"></span>관리자 메모내역</div>
                    <div class="dvr-memo-write">
                        <textarea class="dvr-memo-text" rows="2" placeholder="메모를 입력하세요 (관리자 공용)"></textarea>
                        <button type="button" class="dvr-btn dvr-btn--primary dvr-memo-add">등록</button>
                    </div>
                    <div class="dvr-memo-list">${this._memoRows(res.memos || [])}</div>
                </div>
            </div>`;

        // ── 좌: 신고자 ──
        // 신고자 이름 클릭 → 기사편집. #번호 클릭 → 회원 팝오버. 📱 → 제보자 모바일. 💬 → 불러톡 채팅.
        // 제보자 모바일은 팝오버 메뉴에도 있지만 수발주자 소유자에게만 뜬다 — 발주자 신고자도 열 수 있게 아이콘으로 둔다.
        const repName = this._esc(r.reporter_name || '-');
        const repNameLink = r.mb_no
            ? `<a href="javascript:;" class="dvr-reporter-edit" data-mb="${r.mb_no}" title="기사편집">${repName}</a>`
            : repName;

        // 대표번호(첫번째)/추가번호(나머지) — bad_info.phones 기준. 승인 전이면 대표번호 수정 가능.
        const repPhone    = (Array.isArray(info.phones) && info.phones.length) ? info.phones[0] : (info['연락처'] || '');
        const extraPhones = (Array.isArray(info.phones) && info.phones.length > 1) ? info.phones.slice(1) : [];
        const extraHtml   = extraPhones.length
            ? extraPhones.map(p => `<span class="dvr-extra-chip">${this._esc(Util.phoneNumber(p) || p)}</span>`).join('')
            : '<span class="text-muted">없음</span>';
        const canEditRep  = !RO && r.status !== 'APPROVED' && r.status !== 'RECALLED';
        const repCell = this._repEditing
            ? `<input id="dvr-rep-input" class="dvr-rep-input" inputmode="tel" value="${this._esc(Util.phoneNumber(repPhone) || repPhone)}">`
              + ` <button class="btn btn-primary btn-xs dvr-act" data-act="repSave" data-num="${r.num}">저장</button>`
              + ` <button class="btn btn-outline-secondary btn-xs dvr-act" data-act="repCancel" data-num="${r.num}">취소</button>`
              + ` <span class="dvr-rep-hint">저장하면 대표번호가 변경됩니다</span>`
            : `${this._esc(Util.phoneNumber(repPhone) || repPhone || '-')}`
              + (canEditRep ? ` <button class="btn btn-outline-secondary btn-xs dvr-act" data-act="repEdit" data-num="${r.num}" data-hint="확인 완료 전까지 대표번호를 수정할 수 있습니다. (병합·통화경고 기준 번호)">번호 수정</button>` : '');

        // 같은 회원·같은 대표번호의 다른 제보 — 중복 의심. 비교해 볼 수 있게 새 탭으로 연다.
        const sameReports = res.same_reports || [];
        const sameHtml = sameReports.length ? `
                <div class="dvr-same">
                    <div class="dvr-same-t">⚠️ 같은 회원·같은 번호 제보 ${sameReports.length}건</div>
                    ${sameReports.map(s => {
                        const label = s.recovered_at ? '전액회수' : (this.labels.report[s.status] || s.status);
                        return `<a class="dvr-same-row" href="/adm/tasks?type=devil&open=${Number(s.num)}" target="_blank" rel="noopener">#${Number(s.num)} ${this._esc(label)} · 피해일 ${this._esc(s.damage_day || '-')} · ${Number(s.unpaid_amount).toLocaleString()}원</a>`;
                    }).join('')}
                </div>` : '';

        const left = `
            <div class="dvr-col dvr-col-r">
                <div class="dvr-col-head dvr-head-r">제보자 <span class="dvr-rno">제보번호 #${String(r.num).padStart(4, '0')}</span></div>
                <div class="dvr-col-body">
                ${sameHtml}
                <table class="table table-sm dvr-info">
                    <tr><th width="78">제보자</th><td>${repNameLink}<span class="text-muted">#${$j.F.Admin.Member.Pop.cellNo(r.mb_no)}</span> <a href="javascript:;" class="dvr-reporter" data-mb="${r.mb_no}" title="제보자 모바일"><i class="fas fa-mobile-screen"></i></a> <a href="javascript:;" class="dvr-chat" data-mb="${r.mb_no}" title="불러톡 채팅"><i class="fa fa-comments"></i></a>${r.reporter_phone ? ` <span class="dvr-phone">${this._esc(Util.phoneNumber(r.reporter_phone) || r.reporter_phone)}</span>` : ''}</td></tr>
                    <tr><th>작성일시</th><td>${this._esc(r.created_at || '-')}</td></tr>
                    <tr><th>체불금액</th><td class="dvr-strong">${Number(r.unpaid_amount).toLocaleString()}원</td></tr>
                    <tr><th>회수</th><td>${this._recoverText(r, res.logs)}</td></tr>
                    <tr><th>피해일</th><td>${this._esc(info['피해일'] || '-')}</td></tr>
                    <tr><th>작업장비</th><td>${this._esc(info['작업장비'] || '-')}</td></tr>
                    <tr><th>작업지주소</th><td>${this._esc(info['작업지주소'] || '-')}</td></tr>
                    <tr><th>피해내용</th><td>${this._esc(r.damage_desc || '-')}</td></tr>
                    <tr><th>추가번호<span class="dvr-add-note">검색용</span></th><td>${extraHtml}</td></tr>
                </table>
                <div class="dvr-sec">증빙사진(제보)</div>
                <div class="dvr-thumbs">${thumbs(photosR, 'r')}</div>
                </div>
            </div>`;

        // ── 우: 체불업체(정정 요청자) ──
        // 업체명 옆 정정 화면 보기 링크 (정정대기 = 정정요청 발송된 뒤에만)
        const previewLink = (!RO && r.status === 'DEFENDING')
            ? ` <a href="javascript:;" class="dvr-act" data-view-ok data-act="preview" data-num="${r.num}" data-hint="체불업체가 실제로 보게 되는 정정 요청 페이지를 새 창으로 미리 봅니다.">🔑 소명 화면</a>` : '';
        // 링크 복사 — 정정요청 발송된 신고만. 결정적 토큰 링크(reportDetail 에서 주입)
        const copyLink = (df && df.link)
            ? ` <a href="javascript:;" class="dvr-act" data-view-ok data-act="copyLink" data-link="${this._esc(df.link)}" data-hint="체불업체 정정 요청 페이지 링크를 클립보드에 복사합니다. (문자 등으로 전달)">📋 링크복사</a>` : '';
        // 추가정보(extra_info) — 정정 요청자 신원(type notme). 옛 소명 행은 없을 수 있다
        const ex = (df && df.extra_info) ? df.extra_info : null;
        const isNotme = !!(ex && ex.type === 'notme');
        const notmeRows = isNotme ? `
                    <tr><th colspan="2" style="background:var(--adm-b-fff3cd, #fff3cd);color:var(--adm-c-856404, #856404)">⚠️ 본인 아님·사실 다름 소명 요청</th></tr>
                    <tr><th>요청자명</th><td>${this._esc(ex.name || '-')}</td></tr>` : '';
        const right = `
            <div class="dvr-col dvr-col-d">
                <div class="dvr-col-head dvr-head-d">체불업체 소명 요청</div>
                <div class="dvr-col-body">
                <table class="table table-sm dvr-info">
                    <tr><th width="78">업체명</th><td><span class="dvr-strong">${this._esc(info['이름'] || r.master_co_name || '-')}</span>${previewLink}${copyLink}</td></tr>
                    <tr><th>대표번호</th><td class="dvr-rep-cell">${repCell}</td></tr>
                    <tr><th>사업자번호</th><td>${this._esc(info['사업자번호'] || '-')}</td></tr>
                    <tr><th>대표자</th><td>${this._esc(info['대표자명'] || '-')}</td></tr>
                    <tr><th>사업장주소</th><td>${this._esc(info['사업장주소'] || '-')}</td></tr>
                    ${notmeRows}
                    <tr><th>작성일시</th><td>${df && df.created_at ? this._esc(df.created_at) : '<span class="text-muted">-</span>'}</td></tr>
                    <tr><th>소명사유</th><td style="white-space:pre-wrap;word-break:break-word">${df ? this._esc(df.content || '-') : '<span class="text-muted">-</span>'}</td></tr>
                </table>
                <div class="dvr-sec">증빙사진(소명)</div>
                <div class="dvr-thumbs">${thumbs(photosD, 'd')}</div>
                </div>
            </div>`;

        // 악덕 해제(RELEASED) 건은 신고쪽 화면 전체를 흐리게 — 종결 상태 시각화(액션은 이미 숨김).
        const html = `
            <div class="dvr-body${released ? ' dvr-released' : ''}">
                ${flowBar}
                <div class="dvr-split">${left}${right}</div>
            </div>
        `;

        // 통합모달 임베드: host 컨테이너에 주입 / 단독: JBModal
        if (this.host) this.host.mountReport(html, num, r);
        // 폭: 통합(악덕업체) 모달 안에서 신고 상세가 차지하는 detail 패널 폭에 맞춤(≈930px content).
        // 케이스 모달(1300px)보다 작게 — 단독 신고 모달은 그만큼만 필요.
        else $j.Ui.JBModal.html(this.modalId, html, `악덕제보 #${num}`, { width: '960px' });
    }

    // ── 처리버튼 (단계별) ────────────────────────────────────
    _reportActions = (r, df, RO) => {
        if (RO) return '';
        // 철회(해제)는 제출·정정대기·등록 전 구간에서 가능 — 사유·주체를 모달에서 고른다 (기획 2026-10-01).
        //   수금 완료·등록자 철회 요청은 업체가 정정 요청을 내지 않아도 생기므로 정정 제출 여부로 막지 않는다.
        const recallBtn = `<button class="dvr-btn dvr-btn--line dvr-act" data-act="recall" data-num="${r.num}" data-hint="이 제보를 철회(해제)합니다. 사유·주체를 선택하며, 이미 악덕 등록된 건이면 체불업체에 철회 안내 문자가 갑니다.">철회 · 해제</button>`;
        const rejectBtn = `<button class="dvr-btn dvr-btn--line-danger dvr-act" data-act="reject" data-num="${r.num}" data-hint="증빙 부족·사실 불일치·중복 등으로 이 제보를 반려합니다. (사유는 제보자에게 전달)">제보 반려</button>`;
        if (r.status === 'SUBMITTED') {
            // 제출 단계 — 바로 등록하지 않고 정정대기(정정요청 발송→DEFENDING) 또는 반려
            return `<button class="dvr-btn dvr-btn--primary dvr-act" data-act="notice" data-num="${r.num}" data-hint="체불업체에 소명요청 문자(LMS)를 보내 소명 기회를 줍니다. 업체 쪽 문자·페이지에는 '정정 요청'이라고 적힙니다. (소명대기 단계로 전환)">소명진행</button>`
                 + rejectBtn
                 + recallBtn;
        }
        if (r.status === 'DEFENDING') {
            // 정정대기 단계:
            //  · 회수처리 : 체불업체가 지급 → 악덕 등록 후 즉시 회수(2스텝 원샷)
            //  · 철회·해제: 정정 수용·수금·등록자 요청 등 → 제보 철회 (항상 노출)
            //  - 정정요청 단계(NOTIFIED/DRAFT=미응답) → 자동등록 안내문구 (결과 대기)
            //  - 정정 제출(SUBMITTED)              → 체불등록·회수처리 + 정정반려(_defenseActions)
            //  - 기한 만료(EXPIRED)                → 체불등록
            const ds = df ? df.status : '';
            let h = '';
            if (ds === 'NOTIFIED' || ds === 'DRAFT' || ds === '') {
                h += `<span class="dvr-act-hint">소명 미응답 ${this._noticeDays}일 경과 시 자동 등록</span>`;
            }
            if (ds === 'SUBMITTED' || ds === 'EXPIRED' || ds === 'REJECTED') {
                h += `<button class="dvr-btn dvr-btn--danger dvr-act" data-act="approve" data-num="${r.num}" data-hint="소명 요청을 인정하지 않고 악덕(체불)으로 등록·채택합니다. 전화번호 매칭으로 악덕업체에 병합됩니다. (등록 안내 문자는 나가지 않음)">체불 등록</button>`;
            }
            if (ds === 'SUBMITTED') {
                h += `<button class="dvr-btn dvr-btn--line dvr-act" data-act="approveRecovered" data-num="${r.num}" data-hint="체불업체가 대금을 지급한 경우 — 악덕 등록 후 즉시 전액회수까지 한 번에 처리합니다.">회수 처리</button>`;
            }
            h += rejectBtn + recallBtn;
            return h;
        }
        if (r.status === 'APPROVED') {
            // 케이스 모달은 상단에 마스터가 항상 보이므로 '악덕마스터 보기' 버튼은 두지 않는다.
            const isRec = !!r.recovered_at;
            return `<button class="dvr-btn dvr-btn--${isRec ? 'line' : 'primary'} dvr-act" data-act="recovered" data-num="${r.num}" data-rec="${isRec ? 1 : 0}" data-hint="${isRec ? '전액회수 처리를 취소하고 미회수 상태로 되돌립니다.' : '체불금을 전액 회수했음을 기록합니다. (회수합계에 반영)'}">${isRec ? '회수 취소' : '전액회수 처리'}</button>`
                 + recallBtn;
        }
        return ''; // REJECTED / RECALLED — 종결
    }

    _defenseActions = (r, df, RO) => {
        if (RO) return '';
        const ds = df ? df.status : '';
        // 정정 제출됨 → 반려만. 문자는 안 나가고 담당자가 전화로 안내, 재제출 없음(기획 2026-10-01). 수용은 철회·해제로.
        if (ds === 'SUBMITTED') {
            return `<button class="dvr-btn dvr-btn--line-danger dvr-act" data-act="defenseReject" data-num="${r.num}" data-hint="체불업체의 소명 요청을 반려합니다. 반려 문자는 나가지 않습니다 — 담당자가 업체에 전화로 안내하세요. 업체는 다시 제출할 수 없습니다.">소명 반려</button>`;
        }
        // 미제출(요청발송/작성중) → 버튼 없음. 발송·재발송은 좌측 '정정진행' 버튼.
        return '';
    }

    // ── 이벤트 바인딩(위임, 1회) ──────────────────────────────
    _bind = () => {
        $(document).on('click', '.dvr-photo', (e) => {
            const $t = $(e.currentTarget);
            const gal = $t.data('gal') === 'd' ? this._photosD : this._photosR;
            const i = parseInt($t.attr('data-idx'), 10) || 0;
            if (gal.length) $j.Ui.ZoomPhoto.open(null, { items: gal.map(u => ({ url: u })), index: i });
        });
        $(document).on('click', '.dvr-reporter', (e) => {
            $j.F.Admin.Util.devilMobile($(e.currentTarget).data('mb'));
        });
        // 신고자 이름 클릭 → 기사편집
        $(document).on('click', '.dvr-reporter-edit', (e) => {
            $j.F.Admin.Util.memberEdit($(e.currentTarget).data('mb'));
        });
        // 신고자 불러톡 채팅 (rno=회원번호)
        $(document).on('click', '.dvr-chat', (e) => {
            const mb = $(e.currentTarget).data('mb');
            if (!mb) return;
            if ($j.F.Admin.Util && $j.F.Admin.Util.chat) $j.F.Admin.Util.chat(mb);
            else window.open('/adm/chat?rno=' + mb, 'admChat_' + mb, 'width=400,height=600');
        });
        $(document).on('click', '.dvr-act', (e) => {
            const $b = $(e.currentTarget);
            const fn = this['_act_' + $b.data('act')];
            if (fn) fn($b.data('num'), $b);
        });
        // 주요 버튼 설명 툴팁 — 마우스오버 시 Ui.Tooltip 팝오버(클릭 액션은 그대로 유지, data-hint 로 트리거)
        $(document)
            .on('mouseenter.dvrhint', '[data-hint]', (e) => {
                const el = e.currentTarget;
                $j.Ui.Tooltip.text(el, el.getAttribute('data-hint') || '', { place: 'top' });
            })
            .on('mouseleave.dvrhint', '[data-hint]', () => $j.Ui.Tooltip.hide());
        // 처리내역(변경이력) 아코디언 토글 — 신고 상세 1개만 보이므로 전역 셀렉터 사용
        $(document).on('click', '.dvr-logbtn', (e) => {
            const $b = $(e.currentTarget);
            const open = !$b.hasClass('open');
            $b.toggleClass('open', open);
            $b.find('.dvr-caret').text(open ? '▴' : '▾');
            $('.dvr-memobox').stop(true, true).slideUp(120);        // 메모함은 닫음(상호배타)
            $('.dvr-memobtn').removeClass('open').find('.dvr-caret').text('▾');
            $('.dvr-replog').stop(true, true).slideToggle(140);
        });
        // 메모내역 아코디언 토글
        $(document).on('click', '.dvr-memobtn', (e) => {
            const $b = $(e.currentTarget);
            const open = !$b.hasClass('open');
            $b.toggleClass('open', open);
            $b.find('.dvr-caret').text(open ? '▴' : '▾');
            $('.dvr-replog').stop(true, true).slideUp(120);         // 처리내역은 닫음
            $('.dvr-logbtn').removeClass('open').find('.dvr-caret').text('▾');
            $('.dvr-memobox').stop(true, true).slideToggle(140);
        });
        // 메모 등록
        $(document).on('click', '.dvr-memo-add', () => {
            const $t = $('.dvr-memo-text');
            const memo = String($t.val() || '').trim();
            if (!memo) { $t.focus(); return; }
            $.post(`/adm/ajax/devil/report/${this._num}/memo`, { memo }, (res) => {
                if (!res.result) { Util.swalert(res.message || '메모 등록 실패'); return; }
                $t.val('');
                this._reloadMemos();
            }, 'json');
        });
        // 메모 삭제
        $(document).on('click', '.dvr-memo-del', (e) => {
            const memoNum = $(e.currentTarget).data('num');
            if (!confirm('이 메모를 삭제할까요?')) return;
            $.post(`/adm/ajax/devil/report/${this._num}/memo/${memoNum}/delete`, {}, (res) => {
                if (!res.result) { Util.swalert(res.message || '삭제 실패'); return; }
                this._reloadMemos();
            }, 'json');
        });
    }

    // ── 메모내역 ─────────────────────────────────────────────
    _memoRows = (memos) => {
        if (!memos || !memos.length) return '<div class="dvr-logempty">메모 없음</div>';
        return memos.map(m => `
            <div class="dvr-memoitem">
                <span class="dvr-memot">${this._esc((m.datetime || '').substr(0, 16))}</span>
                <span class="dvr-memom">${this._esc(m.memo || '')}</span>
                <span class="dvr-memoby">${this._esc(m.writer_name || ('#' + m.mb_no))}</span>
                <button type="button" class="dvr-memo-del" data-num="${m.num}" title="삭제">✕</button>
            </div>`).join('');
    }
    // 메모함 부분 갱신(모달 재오픈 없이) — 목록 + 카운트 배지
    _reloadMemos = () => {
        $.get(`/adm/ajax/devil/report/${this._num}/memos`, (res) => {
            if (!res.result) return;
            const memos = res.memos || [];
            $('.dvr-memo-list').html(this._memoRows(memos));
            $('.dvr-memocount').text(memos.length);
        }, 'json');
    }

    // ── 액션 ────────────────────────────────────────────────
    _act_approve = (num) => {
        Util.swalert('체불(악덕)로 등록합니다. 전화번호 매칭으로 악덕마스터에 채택/병합됩니다.\n진행할까요?', () => {
            $.post(`/adm/ajax/devil/report/${num}/approve`, res => this._afterRefresh(res, '체불 등록되었습니다.'), 'json');
        });
    }
    // 회수처리 원샷 — 체불업체가 대금 지급 → 악덕 등록 후 즉시 전액회수(오늘)
    _act_approveRecovered = (num) => {
        Util.swalert('체불업체가 대금을 지급했습니까?\n악덕으로 등록한 뒤 즉시 전액회수(오늘)까지 한 번에 처리합니다.\n진행할까요?', () => {
            $.post(`/adm/ajax/devil/report/${num}/approve-recovered`, res => this._afterRefresh(res, '회수 처리되었습니다.'), 'json');
        });
    }
    _act_reject = (num) => {
        this._openReason('reject', (reason) => {
            $.post(`/adm/ajax/devil/report/${num}/reject`, { reason }, res => this._afterRefresh(res, '반려되었습니다.'), 'json');
        });
    }
    // 철회·해제 — 사유(코드)·주체를 고르는 전용 모달. 서버 어휘: DvReportService::RECALL_REASON / RECALL_ACTOR
    _RECALL_REASONS = [
        { code: 'PAID',       label: '수금 완료',        actor: 'REPORTER' },
        { code: 'REPORTER',   label: '등록자 철회 요청', actor: 'REPORTER' },
        { code: 'CORRECTION', label: '정정에 의한 철회', actor: 'ADMIN' },
        { code: 'ETC',        label: '기타 · 직접입력',  actor: 'ADMIN' },
    ];
    _RECALL_ACTORS = [{ code: 'REPORTER', label: '등록자(제보자)' }, { code: 'ADMIN', label: '관리자' }];
    _act_recall = (num) => {
        this._openRecall((data) => {
            $.post(`/adm/ajax/devil/report/${num}/recall`, data,
                res => this._afterRefresh(res, '철회·해제 처리되었습니다.'), 'json');
        });
    }
    _openRecall = (onConfirm) => {
        this._ensureRecallModal();
        this._recallConfirm = onConfirm;
        this._recallReason = ''; this._recallActor = '';
        $('#dvr-rc-reasons .dvr-rm-chip, #dvr-rc-actors .dvr-rm-chip').removeClass('sel');
        $('#dvr-rc-input').val('').hide();
        $('#dvr-recall-modal').addClass('show');
    }
    _ensureRecallModal = () => {
        if ($('#dvr-recall-modal').length) return;
        const chip = (c, label) => `<button type="button" class="dvr-rm-chip" data-v="${c}">${this._esc(label)}</button>`;
        $(`
            <div class="dvr-reason-modal" id="dvr-recall-modal">
                <div class="dvr-rm-card">
                    <div class="dvr-rm-title">철회(해제) 사유 · 주체</div>
                    <div class="dvr-rm-desc">사유를 고르면 주체가 자동으로 맞춰집니다(바꿀 수 있음). 둘 다 변경이력에 남고, 악덕 등록된 건이면 업체 철회 안내 문자에도 들어갑니다.</div>
                    <div class="dvr-rm-desc" style="margin-top:8px"><b>사유</b></div>
                    <div class="dvr-rm-chips" id="dvr-rc-reasons">${this._RECALL_REASONS.map(x => chip(x.code, x.label)).join('')}</div>
                    <textarea class="dvr-rm-input" id="dvr-rc-input" placeholder="기타 사유 직접입력 (다른 사유를 골랐을 땐 보충 설명, 선택)"></textarea>
                    <div class="dvr-rm-desc" style="margin-top:8px"><b>주체</b> — 누가 철회를 원했나</div>
                    <div class="dvr-rm-chips" id="dvr-rc-actors">${this._RECALL_ACTORS.map(x => chip(x.code, x.label)).join('')}</div>
                    <div class="dvr-rm-actions">
                        <button type="button" class="dvr-btn dvr-btn--line" id="dvr-rc-cancel">취소</button>
                        <button type="button" class="dvr-btn dvr-btn--primary" id="dvr-rc-confirm">철회 처리</button>
                    </div>
                </div>
            </div>`).appendTo('body');
        $('#dvr-rc-reasons').on('click', '.dvr-rm-chip', (e) => {
            const $c = $(e.currentTarget);
            $('#dvr-rc-reasons .dvr-rm-chip').removeClass('sel'); $c.addClass('sel');
            this._recallReason = String($c.data('v'));
            const def = (this._RECALL_REASONS.find(x => x.code === this._recallReason) || {}).actor || '';
            if (def) { this._recallActor = def; $('#dvr-rc-actors .dvr-rm-chip').removeClass('sel').filter(`[data-v="${def}"]`).addClass('sel'); }
            $('#dvr-rc-input').toggle(true);
            if (this._recallReason === 'ETC') $('#dvr-rc-input').focus();
        });
        $('#dvr-rc-actors').on('click', '.dvr-rm-chip', (e) => {
            const $c = $(e.currentTarget);
            $('#dvr-rc-actors .dvr-rm-chip').removeClass('sel'); $c.addClass('sel');
            this._recallActor = String($c.data('v'));
        });
        $('#dvr-rc-confirm').on('click', () => {
            const text = String($('#dvr-rc-input').val() || '').trim();
            if (!this._recallReason) { alert('철회 사유를 선택해 주세요.'); return; }
            if (this._recallReason === 'ETC' && !text) { alert('기타 사유를 입력해 주세요.'); return; }
            if (!this._recallActor) { alert('철회 주체를 선택해 주세요.'); return; }
            $('#dvr-recall-modal').removeClass('show');
            const fn = this._recallConfirm; this._recallConfirm = null;
            if (fn) fn({ reason_code: this._recallReason, reason: text, actor: this._recallActor });
        });
        $('#dvr-rc-cancel').on('click', () => $('#dvr-recall-modal').removeClass('show'));
        $('#dvr-recall-modal').on('click', (e) => { if (e.target.id === 'dvr-recall-modal') $('#dvr-recall-modal').removeClass('show'); });
    }
    // 사유 선택 모달 (제보반려/정정반려 공용) — 프리셋 칩 + 기타 직접입력
    _REASONS = {
        reject:        { title: '제보 반려 사유',    list: ['증빙사진 미첨부/불명확', '체불 정보 불일치', '중복 제보', '체불 사실 확인 불가'] },
        defenseReject: { title: '소명 반려 사유',    list: ['소명 증빙 불충분', '소명 내용 사실과 다름', '본인 아님 확인 불가'] },
    };
    _openReason = (key, onConfirm) => {
        const cfg = this._REASONS[key];
        if (!cfg) return;
        this._ensureReasonModal();
        this._reasonConfirm = onConfirm;
        this._reasonPending = '';
        $('#dvr-rm-title').text(cfg.title);
        $('#dvr-rm-chips').html(
            cfg.list.map(x => `<button type="button" class="dvr-rm-chip" data-v="${this._esc(x)}">${this._esc(x)}</button>`).join('')
            + `<button type="button" class="dvr-rm-chip other" data-v="__other">기타 · 직접입력</button>`
        );
        $('#dvr-rm-input').val('').hide();
        $('#dvr-reason-modal').addClass('show');
    }
    _ensureReasonModal = () => {
        if ($('#dvr-reason-modal').length) return;
        $(`
            <div class="dvr-reason-modal" id="dvr-reason-modal">
                <div class="dvr-rm-card">
                    <div class="dvr-rm-title" id="dvr-rm-title">사유 선택</div>
                    <div class="dvr-rm-desc">사유를 선택하거나 기타로 직접 입력하세요.</div>
                    <div class="dvr-rm-chips" id="dvr-rm-chips"></div>
                    <textarea class="dvr-rm-input" id="dvr-rm-input" placeholder="기타 사유 직접입력"></textarea>
                    <div class="dvr-rm-actions">
                        <button type="button" class="dvr-btn dvr-btn--line" id="dvr-rm-cancel">취소</button>
                        <button type="button" class="dvr-btn dvr-btn--primary" id="dvr-rm-confirm">확인</button>
                    </div>
                </div>
            </div>`).appendTo('body');
        // 이벤트 위임(1회)
        $('#dvr-rm-chips').on('click', '.dvr-rm-chip', (e) => {
            const $c = $(e.currentTarget);
            $('#dvr-rm-chips .dvr-rm-chip').removeClass('sel');
            $c.addClass('sel');
            const v = String($c.data('v'));
            if (v === '__other') { this._reasonPending = '__other'; $('#dvr-rm-input').show().focus(); }
            else { this._reasonPending = v; $('#dvr-rm-input').hide(); }
        });
        $('#dvr-rm-confirm').on('click', () => {
            const reason = this._reasonPending === '__other' ? String($('#dvr-rm-input').val() || '').trim() : this._reasonPending;
            if (!reason) { alert('사유를 선택하거나 입력해 주세요.'); return; }
            $('#dvr-reason-modal').removeClass('show');
            const fn = this._reasonConfirm; this._reasonConfirm = null;
            if (fn) fn(reason);
        });
        $('#dvr-rm-cancel').on('click', () => $('#dvr-reason-modal').removeClass('show'));
        $('#dvr-reason-modal').on('click', (e) => { if (e.target.id === 'dvr-reason-modal') $('#dvr-reason-modal').removeClass('show'); });
    }
    // 정정요청 발송 — 모달 닫지 않고 새로고침 (정정대기 중 같은 버튼을 다시 누르면 재발송, 5분 간격)
    _act_notice = (num) => {
        Util.swalert('체불업체에 소명요청 문자(LMS)를 발송합니다.\n진행할까요?', () => {
            $.post(`/adm/ajax/devil/report/${num}/defense-notice`, res => this._afterRefresh(res, '소명요청을 발송했습니다.'), 'json');
        });
    }
    _act_preview = (num) => {
        window.open(`/adm/devil/report/${num}/defense-preview`, 'dvDefensePreview', 'width=430,height=880');
    }
    // 정정 요청 링크 클립보드 복사
    _act_copyLink = (num, $b) => {
        const link = String($b.data('link') || '');
        if (!link) return;
        const done = () => {
            const u = window.$j && $j.F && $j.F.Admin && $j.F.Admin.Util;
            if (u && u.tsalert) u.tsalert('success', '소명 요청 링크가 복사되었습니다.');
            else alert('소명 요청 링크가 복사되었습니다.');
        };
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(link).then(done, () => window.prompt('아래 링크를 복사하세요', link));
        } else {
            // http/구형 폴백
            const $t = $('<textarea>').val(link).css({ position: 'fixed', top: '-9999px' }).appendTo('body');
            $t[0].select();
            try { document.execCommand('copy'); done(); } catch (e) { window.prompt('아래 링크를 복사하세요', link); }
            $t.remove();
        }
    }
    // 승인 신고 → 채택된 악덕마스터. 통합모달이면 좌측 마스터 pane 펼침, 단독이면 마스터 모달 스택.
    _act_master = (masterNum) => {
        if (this.host && this.host.onShowMaster) { this.host.onShowMaster(masterNum); return; }
        $j.F.Admin.Devil.Master.open(masterNum, { labels: this.labels, onChanged: this.onChanged });
    }
    _act_defenseReject = (num) => {
        this._openReason('defenseReject', (memo) => {
            $.post(`/adm/ajax/devil/report/${num}/defense-review`, { memo },
                res => this._afterRefresh(res, '소명 요청을 반려했습니다. 문자는 나가지 않으니 담당자가 업체에 전화로 안내해 주세요.'), 'json');
        });
    }
    _act_recovered = (num, $b) => {
        // 일부회수 없음 — 전액회수 여부 토글
        const isRec = (parseInt($b.data('rec'), 10) || 0) === 1;
        const next = isRec ? 0 : 1;
        if (!confirm(isRec ? '전액회수를 취소하시겠습니까?' : '전액 회수 처리하시겠습니까?')) return;
        $.post(`/adm/ajax/devil/report/${num}/recovered`, { recovered: next },
            res => this._afterRefresh(res, next ? '전액 회수 처리했습니다.' : '회수를 취소했습니다.'), 'json');
    }

    // ── 액션 후처리 ──────────────────────────────────────────
    _afterClose = (res, okMsg) => {
        if (!res.result) { Util.swalert(res.message || '처리 실패'); return; }
        $j.Ui.JBModal.close && $j.Ui.JBModal.close(this.modalId);
        if (this.onChanged) this.onChanged();
        this._toast(okMsg);
    }
    _afterRefresh = (res, okMsg) => {
        if (!res.result) { Util.swalert(res.message || '처리 실패'); return; }
        if (this.onChanged) this.onChanged();
        this.open(this._num, { readOnly: this.readOnly, modalId: this.modalId, host: this.host }); // 최신 상태로 새로고침(host 유지)
        this._toast(okMsg);
    }
    // host 유지 새로고침(수정모드 플래그는 num 동일하므로 open 에서 보존됨)
    _reopen = () => this.open(this._num, { readOnly: this.readOnly, modalId: this.modalId, host: this.host });

    // ── 대표번호 수정(악덕 승인 전) ─────────────────────────────
    _act_repEdit   = () => { this._repEditing = true;  this._reopen(); }
    _act_repCancel = () => { this._repEditing = false; this._reopen(); }
    _act_repSave   = (num) => {
        const digits = String($('#dvr-rep-input').val() || '').replace(/[^0-9]/g, '');
        if (!/^01[0-9]{8,9}$/.test(digits)) { Util.swalert('올바른 핸드폰 번호(010…)를 입력하세요.'); return; }
        $.post(`/adm/ajax/devil/report/${num}/rep-phone`, { phone: digits }, res => {
            if (!res.result) { Util.swalert(res.message || '대표번호 변경에 실패했습니다.'); return; }
            this._repEditing = false;
            if (this.onChanged) this.onChanged();
            this._reopen();
            this._toast('대표번호를 변경했습니다.');
        }, 'json');
    }
    _toast = (msg) => { $j.Ui.Toast.show(msg, { type: 'success' }); }

    // ── 통합 진행단계 (고정 5슬롯) ───────────────────────────────
    // 항상 5슬롯(제보제출 · 정정요청 · 정정제출 · 확인완료 · 회수처리)을 그리고, 미진행은 회색(pending).
    // 분기/세부 상태만 해당 슬롯의 라벨·색을 치환한다.
    //   · 슬롯2: 정정요청(긍정) ↔ 제보반려(부정)
    //   · 슬롯3: 정정제출(긍정) — 정정대기|작성중|정정제출|정정반려|만료 (현재 상태 1개)
    //   · 슬롯4: 확인완료(긍정) ↔ 철회·해제(부정)
    //   · 슬롯5: 회수처리(회수완료 시 점등)
    // step = [label, state]  state: done | active | pending | neg
    _flowStageHtml = (r, df) => {
        const rs = r.status, ds = df ? df.status : '';
        const recovered = !!r.recovered_at;
        let s1 = ['제보제출', 'pending'];
        let s2 = ['소명요청', 'pending'];
        let s3 = ['소명제출', 'pending'];
        let s4 = ['확인완료', 'pending'];
        let s5 = ['회수처리', 'pending'];

        if (rs === 'SUBMITTED') {
            s1 = ['제보제출', 'active'];
        } else if (rs === 'REJECTED') {                       // 제보반려 — 정정요청 슬롯에 치환
            s1 = ['제보제출', 'done']; s2 = ['제보반려', 'neg'];
        } else if (rs === 'DEFENDING') {
            s1 = ['제보제출', 'done'];
            if (ds === 'DRAFT')          { s2 = ['소명요청', 'done']; s3 = ['작성중', 'active']; }
            else if (ds === 'SUBMITTED') { s2 = ['소명요청', 'done']; s3 = ['소명제출', 'active']; }   // 정정 제출됨 = 관리자 심사대기(현재 단계)
            else if (ds === 'REJECTED')  { s2 = ['소명요청', 'done']; s3 = ['소명반려', 'neg']; }
            else if (ds === 'EXPIRED')   { s2 = ['소명요청', 'done']; s3 = ['만료', 'neg']; s4 = ['확인완료', 'active']; }
            else                         { s2 = ['소명요청', 'done']; s3 = ['소명대기', 'active']; }   // NOTIFIED
        } else if (rs === 'APPROVED') {
            s1 = ['제보제출', 'done']; s2 = ['소명요청', 'done']; s3 = ['소명제출', 'done'];
            if (recovered) { s4 = ['확인완료', 'done']; s5 = ['회수처리', 'active']; }  // 등록 후 전액회수 완료
            else           { s4 = ['확인완료', 'active']; }                             // 등록만(미회수)
        } else if (rs === 'RECALLED') {                       // 철회·해제 — 확인완료 슬롯에 치환
            s1 = ['제보제출', 'done']; s2 = ['소명요청', 'done']; s3 = ['소명제출', 'done']; s4 = ['철회·해제', 'neg'];
        } else {
            s1 = ['제보제출', 'active'];
        }
        return this._renderSteps([s1, s2, s3, s4, s5]);
    }
    _renderSteps = (steps) => {
        const html = steps.map(([label, state], i) => {
            const mark = state === 'done' ? '✓' : state === 'neg' ? '!' : String(i + 1);
            const cls  = state === 'active' ? 'dv-step active'
                       : state === 'neg'    ? 'dv-step active-neg'
                       : state === 'done'   ? 'dv-step done'
                       : 'dv-step';
            return `<span class="${cls}"><i>${mark}</i>${this._esc(label)}</span>`;
        }).join('<span class="dv-step-arrow">›</span>');
        return `<div class="dv-steps">${html}</div>`;
    }

    _injectStyle = () => {
        if (this._styleInjected) return;
        this._styleInjected = true;
        const css = `
/* ⚑ 이 CSS 도 런타임에 head 로 주입된다 (Case.js 와 같은 구조).
   반경은 --adm-radius-*, 재발송 모달 그림자는 --adm-shadow-modal 로 받아 테마를 탄다.
   신고자/체불업체 칸(.dvr-col)이 기본 테마에서도 14px 로 둥글던 게 여기였다.
   남긴 것 — 50%(원형 단계표시), 2px(막대 끝), 그리고 버튼 그림자는 버튼 색을 띠는
   의미색이라 무채색 그림자 토큰으로 바꾸지 않는다.
   주의: 이 안은 템플릿 리터럴이라 백틱을 쓰면 문자열이 끊긴다. */

.dv-steps{display:flex;flex-wrap:wrap;align-items:center;gap:3px;margin:1px 0}
.dv-step{display:inline-flex;align-items:center;gap:5px;padding:4px 11px;border-radius:var(--adm-radius-md);background:var(--adm-b-f1f3f5, #f1f3f5);color:var(--adm-c-868e96, #868e96);font-size:14px;white-space:nowrap}
.dv-step i{display:inline-flex;width:18px;height:18px;border-radius:50%;background:#ced4da;color:#fff;font-style:normal;font-size:11px;align-items:center;justify-content:center}
.dv-step.done{background:var(--adm-b-e7f8ed, #e7f8ed);color:#35bb5d}
.dv-step.done i{background:#35bb5d}
.dv-step.active{background:#3e76ed;color:#fff;font-weight:bold}
.dv-step.active i{background:var(--adm-b-ffffff, #fff);color:var(--adm-c-3e76ed, #3e76ed)}
.dv-step.skipped{background:var(--adm-b-f1f3f5, #f1f3f5);color:#adb5bd;text-decoration:line-through}
.dv-step.skipped i{background:#dee2e6;color:#fff}
.dv-step.active-neg{background:#c92a2a;color:#fff;font-weight:bold}
.dv-step.active-neg i{background:var(--adm-b-ffffff, #fff);color:var(--adm-c-c92a2a, #c92a2a)}
.dv-step-arrow{color:#ced4da;font-size:14px}
.dvr-flow{padding:2px 14px 2px;margin-bottom:2px}
.dvr-flow-top{display:flex;align-items:center;justify-content:space-between;gap:10px 14px;flex-wrap:wrap;margin-bottom:0}
.dvr-flow-top .dvr-stage{margin-bottom:0}
.dvr-flow-right{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.dvr-logbtn{flex:none;display:inline-flex;align-items:center;gap:6px;background:var(--adm-b-ffffff, #fff);border:1px solid #ced4da;border-radius:var(--adm-radius-sm);padding:6px 12px;font-size:14px;font-weight:700;color:var(--adm-c-495057, #495057);cursor:pointer}
.dvr-logbtn.open{border-color:#4285f4;color:var(--adm-c-1971c2, #1971c2);background:var(--adm-b-eaf2ff, #eaf2ff)}
.dvr-logcount{font-size:12px;font-weight:700;color:#fff;background:#adb5bd;border-radius:var(--adm-radius-md);padding:1px 7px}
.dvr-logbtn.open .dvr-logcount{background:#4285f4}
.dvr-replog{margin-top:10px;border:1px solid #e9ecef;border-radius:var(--adm-radius-md);padding:10px 14px}
.dvr-replog-t{font-size:14px;font-weight:700;color:var(--adm-c-343a40, #343a40);margin-bottom:6px;display:flex;align-items:center;gap:6px}
.dvr-replog-t .bar{width:3px;height:15px;background:#4285f4;border-radius:var(--adm-radius-xs)}
.dvr-replog-t small{color:#adb5bd;font-weight:400}
.dvr-logitem{display:flex;align-items:center;gap:10px;font-size:13px;padding:6px 0;border-bottom:1px dashed #edf0f2}
.dvr-logitem:last-child{border-bottom:none}
.dvr-logt{flex:none;width:136px;color:var(--adm-c-868e96, #868e96);white-space:nowrap}
.dvr-logm{flex:1;color:var(--adm-c-343a40, #343a40);min-width:0}
.dvr-logm-r b{color:var(--adm-c-1864ab, #1864ab)}
.dvr-logm-d b{color:var(--adm-c-d9480f, #d9480f)}
.dvr-logmemo{color:var(--adm-c-868e96, #868e96)}
.dvr-logby{flex:none;font-size:12px;font-weight:600;color:var(--adm-c-495057, #495057);background:var(--adm-b-f1f3f5, #f1f3f5);border-radius:var(--adm-radius-md);padding:2px 9px;white-space:nowrap}
.dvr-logempty{color:#adb5bd;font-size:13px;padding:6px 2px}
/* 메모내역 — 처리내역과 나란히(주황 계열) */
.dvr-memobtn{flex:none;display:inline-flex;align-items:center;gap:6px;background:var(--adm-b-ffffff, #fff);border:1px solid #ced4da;border-radius:var(--adm-radius-sm);padding:6px 12px;font-size:14px;font-weight:700;color:var(--adm-c-495057, #495057);cursor:pointer}
.dvr-memobtn.open{border-color:#e8863b;color:var(--adm-c-c2660f, #c2660f);background:var(--adm-b-fff5ec, #fff5ec)}
.dvr-memocount{font-size:12px;font-weight:700;color:#fff;background:#adb5bd;border-radius:var(--adm-radius-md);padding:1px 7px}
.dvr-memobtn.open .dvr-memocount{background:#e8863b}
.dvr-memobox{margin-top:10px;border:1px solid #f3e0cd;border-radius:var(--adm-radius-md);padding:10px 14px;background:var(--adm-b-fffdfa, #fffdfa)}
.dvr-memo-write{display:flex;gap:8px;align-items:flex-start;margin:6px 0 10px}
.dvr-memo-text{flex:1;min-width:0;border:1px solid #e3d3c0;border-radius:var(--adm-radius-sm);padding:8px 10px;font-size:14px;font-family:inherit;resize:vertical}
.dvr-memo-text:focus{outline:none;border-color:#e8863b}
.dvr-memo-add{flex:none;height:auto;align-self:stretch}
.dvr-memoitem{display:flex;align-items:center;gap:10px;font-size:13px;padding:7px 0;border-bottom:1px dashed #f1e6d8}
.dvr-memoitem:last-child{border-bottom:none}
.dvr-memot{flex:none;width:112px;color:var(--adm-c-a98c6b, #a98c6b);white-space:nowrap}
.dvr-memom{flex:1;color:var(--adm-c-343a40, #343a40);min-width:0;white-space:pre-wrap;word-break:break-word}
.dvr-memoby{flex:none;font-size:12px;font-weight:600;color:var(--adm-c-8a5a25, #8a5a25);background:var(--adm-b-fbeee0, #fbeee0);border-radius:var(--adm-radius-md);padding:2px 9px;white-space:nowrap}
.dvr-memo-del{flex:none;border:none;background:transparent;color:var(--adm-c-c0392b, #c0392b);font-size:14px;line-height:1;cursor:pointer;padding:2px 4px;border-radius:var(--adm-radius-xs)}
.dvr-memo-del:hover{background:var(--adm-b-fdecea, #fdecea)}
/* 신고자·체불업체 — 참고파일처럼 라운딩 박스(색상 헤더 + 본문) */
.dvr-split{display:flex;gap:16px;align-items:stretch}
.dvr-col{flex:1 1 0;min-width:0;border:1px solid #e9ecef;border-radius:var(--adm-radius-md);overflow:hidden;background:var(--adm-b-ffffff, #fff)}
.dvr-col-r{border-color:#cfe0fb}
.dvr-col-d{border-color:#fbe0c4}
.dvr-col-head{display:flex;align-items:center;gap:8px;font-weight:700;font-size:14px;padding:9px 14px}
.dvr-col-head::before{content:"";width:8px;height:8px;border-radius:50%;flex:0 0 auto}
.dvr-head-r{background:var(--adm-b-e9f0fe, #e9f0fe);color:var(--adm-c-3e76ed, #3e76ed)}
.dvr-head-r::before{background:#3e76ed}
.dvr-rno{margin-left:auto;font-size:12px;font-weight:700;color:var(--adm-c-495057, #495057);background:var(--adm-b-ffffff, #fff);border:1px solid #cfd8e3;border-radius:var(--adm-radius-md);padding:2px 10px}
.dvr-head-d{background:var(--adm-b-fff3e8, #fff3e8);color:#f97316}
.dvr-head-d::before{background:#f97316}
.dvr-col-body{padding:2px 14px 10px}
.dvr-stage{margin-bottom:8px}
/* 액션 버튼 행 — 진행바(스텝퍼) 아래 별도 줄. 한 줄에 몰지 않고 아래로 내림 */
.dvr-actions{display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin:10px 0 6px}
.dvr-actions:empty{display:none}
.dvr-act-hint{font-size:13px;color:var(--adm-c-868e96, #868e96);margin-left:4px}
/* 악덕 해제(RELEASED) — 신고쪽 화면 흐리게(종결) */
.dvr-released{opacity:.5;filter:grayscale(.4)}
/* 액션 버튼 디자인 (참고 스크린샷) — 라운드 필/아웃라인 */
.dvr-btn{display:inline-flex;align-items:center;justify-content:center;gap:6px;height:36px;padding:0 18px;border-radius:var(--adm-radius-sm);border:1.5px solid transparent;background:var(--adm-b-ffffff, #fff);font-size:14px;font-weight:700;line-height:1;color:var(--adm-c-344054, #344054);cursor:pointer;transition:filter .12s,background .12s,border-color .12s;white-space:nowrap}
.dvr-btn:focus{outline:none}
.dvr-btn:active{transform:translateY(.5px)}
/* 체불 등록 등 — 강조(로즈 필) */
.dvr-btn--danger{background:#ee4d6a;border-color:#ee4d6a;color:#fff;box-shadow:0 1px 2px rgba(238,77,106,.25)}
.dvr-btn--danger:hover{filter:brightness(.95)}
/* 정정진행·전액회수 등 — 파랑 필 */
.dvr-btn--primary{background:#3e76ed;border-color:#3e76ed;color:#fff;box-shadow:0 1px 2px rgba(62,118,237,.25)}
.dvr-btn--primary:hover{filter:brightness(.95)}
/* 회수 처리·정정·해제·회수 취소 — 회색 아웃라인 */
.dvr-btn--line{background:var(--adm-b-ffffff, #fff);border-color:#d0d5dd;color:var(--adm-c-344054, #344054)}
.dvr-btn--line:hover{background:var(--adm-b-f9fafb, #f9fafb);border-color:#b9c0cc}
/* 정정 반려·제보 반려 — 빨강 아웃라인 */
.dvr-btn--line-danger{background:var(--adm-b-ffffff, #fff);border-color:#f3899a;color:var(--adm-c-e5384a, #e5384a)}
.dvr-btn--line-danger:hover{background:var(--adm-b-fff5f6, #fff5f6);border-color:#ee6d83}
/* 사유 선택 모달 (반려/정정·해제) — 프리셋 칩 + 기타 직접입력 */
.dvr-reason-modal{position:fixed;inset:0;background:rgba(0,0,0,.42);display:none;align-items:center;justify-content:center;z-index:var(--z-index-popover,1065)}
.dvr-reason-modal.show{display:flex}
.dvr-rm-card{background:var(--adm-b-ffffff, #fff);border-radius:var(--adm-radius-md);padding:22px;width:min(calc(90vw / var(--adm-zoom, 1)),440px);box-shadow:var(--adm-shadow-modal)}
.dvr-rm-title{font-size:15px;font-weight:700;color:var(--adm-c-212529, #212529)}
.dvr-rm-desc{font-size:12.5px;color:#adb5bd;margin:4px 0 14px}
.dvr-rm-chips{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:12px}
.dvr-rm-chip{border:1px solid #dee2e6;background:var(--adm-b-ffffff, #fff);border-radius:var(--adm-radius-sm);padding:8px 12px;font-size:13px;cursor:pointer;font-family:inherit;color:var(--adm-c-212529, #212529)}
.dvr-rm-chip:hover{border-color:#ced4da}
.dvr-rm-chip.sel{border-color:#ee4d6a;background:var(--adm-b-ffe9ee, #ffe9ee);color:var(--adm-c-d6336c, #d6336c);font-weight:600}
.dvr-rm-chip.other{border-style:dashed}
.dvr-rm-input{display:none;width:100%;border:1px solid #dee2e6;border-radius:var(--adm-radius-sm);padding:10px 12px;font-size:14px;font-family:inherit;min-height:64px;resize:none;margin-bottom:12px;box-sizing:border-box}
.dvr-rm-actions{display:flex;justify-content:flex-end;gap:8px}
.dvr-info{font-size:14px;width:100%;margin-bottom:6px}
.dvr-strong{font-weight:700;font-size:14px;color:var(--adm-c-212529, #212529)}
.dvr-info th{width:88px;font-size:14px;color:var(--adm-c-868e96, #868e96);font-weight:500;background:transparent;text-align:left;vertical-align:top;white-space:nowrap;padding:6px 10px 6px 4px;border-top:0;border-bottom:1px solid #f1f3f5}
.dvr-info th[colspan]{width:auto;white-space:normal;word-break:break-word}
.dvr-info td{font-size:14px;color:var(--adm-c-212529, #212529);vertical-align:top;padding:6px 4px;border-top:0;border-bottom:1px solid #f1f3f5}
.dvr-info tr:last-child th,.dvr-info tr:last-child td{border-bottom:0}
.dvr-rep-input{border:1px solid #f06595;border-radius:var(--adm-radius-xs);padding:5px 9px;font-size:14px;width:150px;font-family:inherit;vertical-align:middle}
.dvr-rep-hint{display:block;margin-top:4px;font-size:11px;color:var(--adm-c-e64980, #e64980)}
.dvr-extra-chip{display:inline-block;background:var(--adm-b-f1f3f5, #f1f3f5);color:var(--adm-c-495057, #495057);border-radius:var(--adm-radius-sm);padding:2px 9px;margin:1px 5px 1px 0;font-size:14px;font-weight:600}
.dvr-add-note{margin-left:5px;font-size:10px;color:#adb5bd;font-weight:400}
.dvr-same{background:var(--adm-b-fff3cd, #fff3cd);color:var(--adm-c-856404, #856404);border-radius:var(--adm-radius-sm);padding:8px 10px;margin-bottom:8px;font-size:13px}
.dvr-same-t{font-weight:700;margin-bottom:2px}
.dvr-same-row{display:block;color:inherit;text-decoration:underline}
.dvr-sec{font-size:13px;font-weight:bold;color:var(--adm-c-495057, #495057);margin:8px 0 4px}
.dvr-thumbs{display:flex;flex-wrap:wrap}
.dvr-photo{width:74px;height:74px;object-fit:cover;border:1px solid #ddd;border-radius:var(--adm-radius-xs);margin:0 6px 6px 0;cursor:pointer}
.dvr-reporter-edit{color:var(--adm-c-1971c2, #1971c2);font-weight:700;cursor:pointer;text-decoration:none}
.dvr-reporter-edit:hover{text-decoration:underline}
.dvr-phone{color:var(--adm-c-495057, #495057);font-weight:600;margin-left:2px}
.dvr-chat{color:var(--adm-c-1971c2, #1971c2);margin-left:4px;cursor:pointer}
.dvr-chat:hover{color:var(--adm-c-1864ab, #1864ab)}
.dvr-master{margin-top:12px;padding:8px 12px;background:var(--adm-b-f8f9fa, #f8f9fa);border-radius:var(--adm-radius-xs);font-size:13px}
@media(max-width:760px){.dvr-split{flex-direction:column}}
`;
        $('<style>').text(css).appendTo('head');
    }

    // ── 헬퍼 ────────────────────────────────────────────────
    _esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    _badge = (s, kind) => {
        const map = kind === 'defense' ? this.labels.defense : this.labels.report;
        return `<span class="badge-status badge-${s}">${this._esc((map && map[s]) || s)}</span>`;
    }
    _labelMaster = (s) => (this.labels.master && this.labels.master[s]) || s || '-';
    _logSide = (t) => t === 'DEFENSE' ? '<span style="color:var(--adm-c-d9480f, #d9480f)">소명</span>'
                    : t === 'REPORT'  ? '<span style="color:var(--adm-c-1864ab, #1864ab)">제보</span>'
                    : this._esc(t || '-');
    _logAction = (t, a) => { const k = (t || '') + '/' + (a || ''); return this._esc(this.LOG_LABELS[k] || k); }
    _logActor = (l) => {
        if (l.manager_name) return this._esc(l.manager_name);
        if (String(l.manage_num) !== '0') return '#' + l.manage_num;
        if (l.rel_action === 'EXPIRE') return '시스템(자동)';
        if (l.rel_table === 'DEFENSE') return '체불업체';
        if (l.rel_table === 'REPORT')  return '제보자';
        if (l.rel_table === 'RECOVER') return '제보자';   // 관리자번호 0 인 회수 = 제보자가 앱에서 누른 것 (관리자 회수는 번호가 남는다)
        return '시스템';
    }
    /** 상세 「회수」 칸 — 전액회수 (회수일) · 누가(제보자 / 관리자 이름) · 처리 시각. 처리내역(최신순)의 첫 회수 로그가 마지막 회수다 */
    _recoverText = (r, logs) => {
        if (!r.recovered_at) return '미회수';
        const l  = (logs || []).find(x => x.rel_table === 'RECOVER' && x.rel_action === 'RECOVER');
        const by = l ? ` · ${this._logActor(l)} <span class="text-muted">${this._esc((l.created_at || '').substr(0, 16))}</span>` : '';
        return '전액회수 (' + this._esc(String(r.recovered_at).substr(0, 10)) + ')' + by;
    }
}
